import { useState, useMemo } from 'react';
import styled from 'styled-components';

import { InputText } from 'ui/inputs';
import { spacing, colors, smallShadow } from 'ui/theme';
import { Table, sortRows } from 'ui/table';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { pxToRem } from 'utils/convert';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { ASCENDING } from 'constants/sortDirections';
import { TableSkeleton } from './TableSkeleton';
import { configureColumns } from './configureColumns';
import { CsvExportButton } from './CsvExportButton';
import { FeatureFlag } from '../../types';

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${spacing.half};
`;

const StyledInput = styled(InputText)`
  width: ${pxToRem(350)};
`;

const StyledTable = styled(Table)`
  border: ${spacing.single} solid ${colors.grey25};
  ${smallShadow};
`;

const StyledTableHeader = styled(TableHeader)`
  position: sticky;
  top: ${pxToRem(100)};
  ${smallShadow};
  z-index: 10;
`;

type FeatureFlagTableProps = {
  featureFlags: FeatureFlag[];
  onOpenChangeLog: (ff: FeatureFlag) => void;
  onOpenEdit: (ff: FeatureFlag) => void;
  onImport: (ff: FeatureFlag) => void;
};

export const FeatureFlagsTable = ({
  featureFlags,
  onOpenChangeLog,
  onOpenEdit,
  onImport,
}: FeatureFlagTableProps) => {
  const [debouncedFilter, filter, setFilter] = useDebouncedState('', 800);
  const [sortedBy, setSortedBy] = useState('feature');
  const [sortDirection, setSortDirection] = useState(ASCENDING);

  const sortedRows = useMemo(() => {
    const rows = filter.length
      ? featureFlags.filter((row) => row.feature.toLowerCase().includes(filter.toLowerCase()))
      : featureFlags;
    return sortRows(rows, sortedBy as keyof FeatureFlag, sortDirection);
  }, [debouncedFilter, featureFlags, sortDirection, sortedBy]);

  const columns = useMemo(
    () => configureColumns(onOpenEdit, onOpenChangeLog, onImport),
    [onOpenEdit, onOpenChangeLog, onImport],
  );

  return (
    <>
      <ActionsContainer>
        <StyledInput
          placeholder={'Filter feature flags...'}
          hasError={false}
          id="featureFlagFilterInput"
          onChange={setFilter}
          value={filter}
        />
        <CsvExportButton sortedFeatures={sortedRows} />
      </ActionsContainer>
      <StyledTable>
        <StyledTableHeader
          columns={columns}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
        />
        {debouncedFilter === filter && <TableRows rows={sortedRows} columns={columns} />}
      </StyledTable>
      {debouncedFilter !== filter && <TableSkeleton />}
    </>
  );
};
